import { FC, useMemo } from 'react';
import { isShipmentUnderReview } from 'src/utils/invoiceStatus';
import { useSearchParams } from 'react-router-dom';
import { useUserContext } from 'src/use-cases/user/userDetails';
import { DeviationDto } from 'src/generated/services/TFinancialApi';
import { useTourContext } from 'src/presentations/guided-tours';
import { UCTDeviationsMainProps } from './types';
import { DeviationDecision } from './Decisions';
import { DeviationInfo } from '../deviationInfo/DeviationInfo';
import { DeviationsTabs } from '../../DeviationsTabs';
import { NoticeContainer } from '../../Notice';
import { useShipmentSelector } from '../../ShipmentSelector';
import { useSelectedDeviation } from './hooks/useSelectedDeviation';

export const ProcurementDeviationsMain: FC<UCTDeviationsMainProps> = (props) => {
  const { invoiceSummary, shipmentInvoiceId } = props;
  const [searchParams] = useSearchParams();
  const userDetails = useUserContext();
  const { shipment } = useShipmentSelector();
  const tour = useTourContext().appTour.registerTour('actionDetails');

  // Get state from context
  const { selectedDeviation, setSelectedDeviation } = useSelectedDeviation();

  const deviations = useMemo(() => shipment?.deviations ?? [], [shipment]);

  const handleChangeDeviation = (data: DeviationDto) => {
    setSelectedDeviation(data);
  };

  const isFromInternal = searchParams.get('ref') === 'internal';
  let isUnderReview = isShipmentUnderReview(invoiceSummary, shipmentInvoiceId);
  if (isUnderReview && userDetails?.isSystemAdmin && isFromInternal) {
    isUnderReview = false;
  }

  // Check if notice should be shown
  const isSupplyChain = invoiceSummary?.currentMainRole === 'SUPPLY_CHAIN';
  const isFinance = invoiceSummary?.currentMainRole === 'FINANCE';
  const isGreaterThanThreshold = selectedDeviation?.isGreaterThanThreshold === true;
  const shouldShowNotice = !isUnderReview && (isSupplyChain || isFinance) && isGreaterThanThreshold;

  // Build dynamic threshold message with bold amount
  const thresholdMessage = useMemo(() => {
    const thresholdAmount = shipment?.deviationThreshold ? `${shipment.deviationThreshold.currency} ${shipment.deviationThreshold.threshold}` : '';

    if (shipment?.deviationThreshold) {
      if (isFinance) {
        return (
          <>
            This deviation amount is greater than <strong>{thresholdAmount}</strong>. Please review the decision from Supply Chain Manager.
          </>
        );
      }
      return (
        <>
          This deviation amount is greater than <strong>{thresholdAmount}</strong>. If you accept it, it will be sent to Finance for review.
        </>
      );
    }

    if (isFinance) {
      return 'This deviation amount is greater than the threshold. Please review the decision from Supply Chain Manager.';
    }
    return 'This deviation amount is greater than the threshold. If you accept it, it will be sent to Finance for review.';
  }, [shipment?.deviationThreshold, isFinance]);

  return (
    <>
      <DeviationsTabs deviations={deviations} selectedDeviation={selectedDeviation} onChange={handleChangeDeviation} />
      {selectedDeviation && (
        <div className='overflow-y-scroll flex-1'>
          <div id={tour.registerStep('chargeDeviationsTable')}>
            <DeviationInfo deviation={selectedDeviation} />
          </div>
          {!isUnderReview && (
            <div className='mt-20 mb-20 pb-24 xl:mb-24 px-20 xl:px-24 border-t border-neutral-20 pt-30 xl:pt-20'>
              {shouldShowNotice && <NoticeContainer className='!mt-0 mb-32' text={thresholdMessage} />}
              <div id={tour.registerStep('deviationQuestionnaire')}>
                <DeviationDecision invoiceSummary={invoiceSummary} selectedDeviation={selectedDeviation} />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
